import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api`;

// Fetches the user's effective permission map from the backend.
// Called once per session from ProtectedRoute — Redux state is in-memory only,
// so every full page refresh clears `loaded` and triggers a fresh DB fetch.
export const fetchPermissions = createAsyncThunk(
  'permissions/fetch',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No token found');

    try {
      const response = await fetch(`${BASE_URL}/getPermissions.php`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Non-JSON response or 404 means the backend endpoint isn't deployed yet.
      // Fulfill with empty permissions so the user can still reach the app.
      let data;
      try {
        data = await response.json();
      } catch {
        return {};
      }

      if (response.status === 404 || data.status !== 'success') {
        if (response.status === 404) return {};
        return rejectWithValue(data.message || `HTTP ${response.status}`);
      }

      return data.permissions; // { "clients.view": true, "clients.add": false, ... }
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

const initialState = {
  data: {},       // flat map: { "clients.view": true, ... }
  loaded: false,  // true after first successful fetch
  loading: false,
  error: null,
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    // Call on logout to wipe the in-memory permissions
    clearPermissions() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loaded = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Permission fetch failed';
        // loaded stays false — ProtectedRoute will not render until fixed
      });
  },
});

export const { clearPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;
